import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, Check, Crown, Users, User, CreditCard, 
  Calendar, Sparkles, AlertCircle
} from 'lucide-react';
import Footer from '@/components/layout/Footer';

// Room images from existing retreat page
const ROOM_IMAGES = {
  dome: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/eif7vw9m_geodesic%20dome.jpeg',
  bungalow: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/72j2xc0z_exotic%20bungalow.png',
  villa: 'https://customer-assets.emergentagent.com/job_88aefb87-8a66-4717-a035-491dc0a3d7b5/artifacts/shh7wly6_Luxury%20Villa.png'
};

// Room data with inventory and pricing
const ROOMS = [
  {
    id: 'geodesic-dome',
    name: 'Sacred Geodesic Domes',
    icon: '🏕️',
    image: ROOM_IMAGES.dome,
    description: 'Nestled in the lush rainforest along our private river, these domes invite you to reconnect with nature.',
    features: ['King Bed', 'Private jungle deck', 'Open-air rainforest shower', 'Full AC'],
    pricePerPerson: 5297,
    occupancy: 'double',
    occupancyLabel: 'Double Occupancy',
    inventory: 12,
    bedType: 'King Beds',
    deposit: 500,
    installments: 4,
    // Ontraport form URLs - to be updated
    forms: {
      fullPay: 'ONTRAPORT_DOME_FULLPAY_URL',
      paymentPlan: 'ONTRAPORT_DOME_PAYMENTPLAN_URL',
      fullPayVip: 'ONTRAPORT_DOME_FULLPAY_VIP_URL',
      paymentPlanVip: 'ONTRAPORT_DOME_PAYMENTPLAN_VIP_URL'
    }
  },
  {
    id: 'exotic-bungalow',
    name: 'Exotic Jungle Bungalows',
    icon: '🌴',
    image: ROOM_IMAGES.bungalow,
    description: 'Bali-inspired bungalows with spacious bathrooms opening to sweeping jungle views.',
    features: ['Twin Beds', 'Jungle deck', 'Soaking tub with views', 'AC available'],
    pricePerPerson: 4997,
    occupancy: 'double',
    occupancyLabel: 'Double Occupancy',
    inventory: 7,
    bedType: 'Twin Beds',
    deposit: 500,
    installments: 4,
    note: 'Single occupancy sold out',
    forms: {
      fullPay: 'ONTRAPORT_BUNGALOW_FULLPAY_URL',
      paymentPlan: 'ONTRAPORT_BUNGALOW_PAYMENTPLAN_URL',
      fullPayVip: 'ONTRAPORT_BUNGALOW_FULLPAY_VIP_URL',
      paymentPlanVip: 'ONTRAPORT_BUNGALOW_PAYMENTPLAN_VIP_URL'
    }
  },
  {
    id: 'luxury-villa',
    name: 'Luxurious Private Villas',
    icon: '🏡',
    image: ROOM_IMAGES.villa,
    description: 'Hand-carved Balinese treasures - 170-180 year old villas with private plunge pools.',
    features: ['King Bed', 'Private plunge pool', 'Ocean & rainforest views', 'Open-air design'],
    pricePerPerson: 6597,
    occupancy: 'single',
    occupancyLabel: 'Single Occupancy',
    inventory: 2,
    bedType: 'King Bed',
    deposit: 1000,
    installments: 4,
    villaOptions: ['The Jogolo House', 'The Potoo House'],
    forms: {
      fullPay: 'ONTRAPORT_VILLA_FULLPAY_URL',
      paymentPlan: 'ONTRAPORT_VILLA_PAYMENTPLAN_URL',
      fullPayVip: 'ONTRAPORT_VILLA_FULLPAY_VIP_URL',
      paymentPlanVip: 'ONTRAPORT_VILLA_PAYMENTPLAN_VIP_URL'
    }
  }
];

const VIP_UPGRADE = {
  price: 1500,
  features: [
    'Priority access to all experiences',
    'Exclusive VIP-only gatherings',
    'Premium amenity package',
    'Personal concierge service'
  ],
  form: 'ONTRAPORT_VIP_ONLY_URL'
};

const RetreatBookingPage = () => {
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [paymentOption, setPaymentOption] = useState('full'); // 'full' or 'plan'
  const [addVip, setAddVip] = useState(false);
  const [selectedVilla, setSelectedVilla] = useState('');

  // Calculate payment plan details
  const getPaymentPlanDetails = (room) => {
    if (!room) return null;
    const remaining = room.pricePerPerson - room.deposit;
    const monthlyPayment = remaining / room.installments;
    return {
      deposit: room.deposit,
      monthly: monthlyPayment.toFixed(2),
      installments: room.installments
    };
  };

  // Get total price
  const getTotalPrice = () => {
    if (!selectedRoom) return 0;
    let total = selectedRoom.pricePerPerson;
    if (addVip) total += VIP_UPGRADE.price;
    return total;
  };

  // Get checkout URL based on selections
  const getCheckoutUrl = () => {
    if (!selectedRoom) return '#';
    
    const formKey = paymentOption === 'full' 
      ? (addVip ? 'fullPayVip' : 'fullPay')
      : (addVip ? 'paymentPlanVip' : 'paymentPlan');
    
    return selectedRoom.forms[formKey];
  };

  const handleCheckout = () => {
    const url = getCheckoutUrl();
    if (url && !url.includes('ONTRAPORT')) {
      window.open(url, '_blank');
    } else {
      alert('Booking forms are being configured. Please check back soon!');
    }
  };

  const paymentPlan = selectedRoom ? getPaymentPlanDetails(selectedRoom) : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sand/30 to-white">
      {/* Header */}
      <div className="bg-forest text-white py-4">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/costa-rica-retreat" className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm">
            <ArrowLeft className="h-4 w-4" />
            Back to Retreat Details
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Title */}
        <div className="text-center mb-10">
          <Badge className="bg-terracotta/10 text-terracotta mb-4">June 26 - July 1, 2026</Badge>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-forest mb-3">
            Book Your Retreat Experience
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Just Be: Soft Power™ Retreat at Imiloa Institute, Costa Rica
          </p>
        </div>

        {/* Step 1: Select Room */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
            <h2 className="font-heading text-xl md:text-2xl text-forest">Select Your Accommodation</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {ROOMS.map((room) => (
              <Card 
                key={room.id}
                className={`cursor-pointer transition-all duration-300 overflow-hidden ${
                  selectedRoom?.id === room.id 
                    ? 'ring-2 ring-forest shadow-xl scale-[1.02]' 
                    : 'hover:shadow-lg hover:scale-[1.01]'
                } ${room.inventory === 0 ? 'opacity-60' : ''}`}
                onClick={() => room.inventory > 0 && setSelectedRoom(room)}
                data-testid={`room-${room.id}`}
              >
                {/* Room Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={room.image} 
                    alt={room.name}
                    className="w-full h-full object-cover"
                  />
                  {/* Inventory Badge */}
                  <div className="absolute top-3 right-3">
                    {room.inventory > 0 ? (
                      <Badge className={`${room.inventory <= 3 ? 'bg-red-500' : 'bg-forest'} text-white`}>
                        {room.inventory} {room.inventory === 1 ? 'spot' : 'spots'} left
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-500 text-white">Sold Out</Badge>
                    )}
                  </div>
                  {/* Selected Check */}
                  {selectedRoom?.id === room.id && (
                    <div className="absolute top-3 left-3 h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center">
                      <Check className="h-5 w-5" />
                    </div>
                  )}
                </div>

                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-heading text-lg text-forest">{room.icon} {room.name}</h3>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">{room.description}</p>
                  
                  {/* Occupancy */}
                  <div className="flex items-center gap-2 mb-3">
                    {room.occupancy === 'double' ? (
                      <Users className="h-4 w-4 text-forest" />
                    ) : (
                      <User className="h-4 w-4 text-forest" />
                    )}
                    <span className="text-sm text-gray-600">{room.occupancyLabel}</span>
                  </div>

                  {/* Features */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {room.features.slice(0, 3).map((feature, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">{feature}</Badge>
                    ))}
                  </div>

                  {/* Price */}
                  <div className="border-t pt-3">
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-forest">${room.pricePerPerson.toLocaleString()}</span>
                      <span className="text-gray-500 text-sm">per person</span>
                    </div>
                    {room.note && (
                      <p className="text-xs text-terracotta mt-1">{room.note}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Step 2: Payment Option (only show if room selected) */}
        {selectedRoom && (
          <div className="mb-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
              <h2 className="font-heading text-xl md:text-2xl text-forest">Choose Payment Option</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {/* Full Pay */}
              <Card 
                className={`cursor-pointer transition-all ${
                  paymentOption === 'full' ? 'ring-2 ring-forest shadow-lg' : 'hover:shadow-md'
                }`}
                onClick={() => setPaymentOption('full')}
                data-testid="payment-full"
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <CreditCard className="h-6 w-6 text-forest" />
                      <div>
                        <h3 className="font-heading text-lg text-forest">Pay in Full</h3>
                        <p className="text-sm text-gray-500">One-time payment</p>
                      </div>
                    </div>
                    <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${
                      paymentOption === 'full' ? 'border-forest bg-forest' : 'border-gray-300'
                    }`}>
                      {paymentOption === 'full' && <Check className="h-4 w-4 text-white" />}
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-forest">
                    ${selectedRoom.pricePerPerson.toLocaleString()}
                  </div>
                </CardContent>
              </Card>

              {/* Payment Plan */}
              <Card 
                className={`cursor-pointer transition-all ${
                  paymentOption === 'plan' ? 'ring-2 ring-forest shadow-lg' : 'hover:shadow-md'
                }`}
                onClick={() => setPaymentOption('plan')}
                data-testid="payment-plan"
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-6 w-6 text-forest" />
                      <div>
                        <h3 className="font-heading text-lg text-forest">Payment Plan</h3>
                        <p className="text-sm text-gray-500">Split into payments</p>
                      </div>
                    </div>
                    <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${
                      paymentOption === 'plan' ? 'border-forest bg-forest' : 'border-gray-300'
                    }`}>
                      {paymentOption === 'plan' && <Check className="h-4 w-4 text-white" />}
                    </div>
                  </div>
                  {paymentPlan && (
                    <div>
                      <div className="text-2xl font-bold text-forest mb-1">
                        ${paymentPlan.deposit.toLocaleString()} <span className="text-base font-normal text-gray-500">down</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        + {paymentPlan.installments} payments of ${paymentPlan.monthly}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">Due 30 days apart before June 26</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 3: VIP Upgrade (only show if room selected) */}
        {selectedRoom && (
          <div className="mb-10 animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ animationDelay: '150ms' }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
              <h2 className="font-heading text-xl md:text-2xl text-forest">VIP Upgrade (Optional)</h2>
            </div>

            <Card 
              className={`cursor-pointer transition-all ${
                addVip ? 'ring-2 ring-terracotta shadow-lg bg-terracotta/5' : 'hover:shadow-md'
              }`}
              onClick={() => setAddVip(!addVip)}
              data-testid="vip-upgrade"
            >
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                      addVip ? 'bg-terracotta text-white' : 'bg-terracotta/10 text-terracotta'
                    }`}>
                      <Crown className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl text-forest mb-1">VIP Experience Upgrade</h3>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {VIP_UPGRADE.features.map((feature, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-white">{feature}</Badge>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Full payment only - no payment plan available for VIP
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-2xl font-bold text-terracotta">+${VIP_UPGRADE.price.toLocaleString()}</div>
                    </div>
                    <div className={`h-6 w-6 rounded border-2 flex items-center justify-center ${
                      addVip ? 'border-terracotta bg-terracotta' : 'border-gray-300'
                    }`}>
                      {addVip && <Check className="h-4 w-4 text-white" />}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Villa Selection (if luxury villa selected) */}
        {selectedRoom?.id === 'luxury-villa' && (
          <div className="mb-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-8 w-8 bg-forest text-white rounded-full flex items-center justify-center text-sm font-bold">
                <Sparkles className="h-4 w-4" />
              </div>
              <h2 className="font-heading text-xl md:text-2xl text-forest">Select Your Villa</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {selectedRoom.villaOptions.map((villa) => (
                <Card 
                  key={villa}
                  className={`cursor-pointer transition-all ${
                    selectedVilla === villa ? 'ring-2 ring-forest shadow-lg' : 'hover:shadow-md'
                  }`}
                  onClick={() => setSelectedVilla(villa)}
                  data-testid={`villa-${villa.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <CardContent className="p-5 flex items-center justify-between">
                    <div>
                      <h3 className="font-heading text-lg text-forest">🌿 {villa}</h3>
                      <p className="text-sm text-gray-500">1 King Bed • Private Plunge Pool</p>
                    </div>
                    <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${
                      selectedVilla === villa ? 'border-forest bg-forest' : 'border-gray-300'
                    }`}>
                      {selectedVilla === villa && <Check className="h-4 w-4 text-white" />}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Order Summary & Checkout */}
        {selectedRoom && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ animationDelay: '300ms' }}>
            <Card className="bg-forest text-white overflow-hidden">
              <CardContent className="p-6 md:p-8">
                <h3 className="font-heading text-xl mb-6">Order Summary</h3>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-white/80">{selectedRoom.name}</span>
                    <span>${selectedRoom.pricePerPerson.toLocaleString()}</span>
                  </div>
                  {addVip && (
                    <div className="flex justify-between">
                      <span className="text-white/80">VIP Upgrade</span>
                      <span>+${VIP_UPGRADE.price.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="border-t border-white/20 pt-3 flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span>${getTotalPrice().toLocaleString()}</span>
                  </div>
                  
                  {paymentOption === 'plan' && !addVip && paymentPlan && (
                    <div className="bg-white/10 rounded-lg p-4 mt-4">
                      <p className="text-sm text-white/80 mb-2">Payment Schedule:</p>
                      <p className="font-medium">Today: ${paymentPlan.deposit.toLocaleString()} deposit</p>
                      <p className="text-sm text-white/70">Then {paymentPlan.installments} payments of ${paymentPlan.monthly}</p>
                    </div>
                  )}
                  
                  {addVip && paymentOption === 'plan' && (
                    <div className="bg-terracotta/20 rounded-lg p-4 mt-4 flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <p className="text-sm">VIP upgrade requires full payment. You'll be charged the full amount today.</p>
                    </div>
                  )}
                </div>

                <Button 
                  size="lg" 
                  className="w-full bg-terracotta hover:bg-terracotta/90 text-white h-14 text-lg"
                  onClick={handleCheckout}
                  data-testid="checkout-btn"
                >
                  Proceed to Checkout
                </Button>

                <p className="text-center text-white/60 text-sm mt-4">
                  You'll be redirected to our secure payment portal
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* No Selection Prompt */}
        {!selectedRoom && (
          <div className="text-center py-12 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p>Select an accommodation above to continue</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default RetreatBookingPage;
